/**
 * Shader code and uniform values for design material layer main mask (aka SDF mask).
 * @module
 */
import { MAX_SDF_PATTERNS } from "./designMaterialDefaults";
import type { DesignMaterialLoadOptions, MaterialParamsWithoutSdf } from "./designMaterialRuntimeConfig";
import { DetailRenderMode } from "./designMaterialRuntimeConfig";
import type { DesignMaterialShaderChunks } from "./designMaterialShaderChunks";
import { OUTSIDE_RANDOM_MASK_VALUE } from "./shaders/utils";

export function evaluateMainMaskShaderChunk(
  options: DesignMaterialLoadOptions,
  detailRenderMode: DetailRenderMode,
  finalParams: MaterialParamsWithoutSdf,
) {
  if (
    (!options.disableSDF && detailRenderMode === DetailRenderMode.ParallaxMap) ||
    (finalParams.heightMapEnabled && finalParams.heightMapName)
  ) {
    return `
    ${
      ""
      // If we don't have height variation (parallaxScale is 0), then take just one random mask sample in the center.
      // Otherwise take samples in a 2x2 grid.
    }
    vec2 mainMaskUv = parallaxMapUvh.xy - 0.5 * float(parallaxScale > 0.0) * vec2(dmMainMaskTextureInvSize);
    vec4 randomMaskSample = texture2D(dmMainMaskTexture, mainMaskUv);
    if (parallaxMapUvh.z > 1.0 / 255.0) {
      ${
        ""
        // In case we're above the 0 height level, then we're inside SDF "brick" rather than "grout" area, and using the
        // random mask value from the "brick" is more accurate. The brick random mask values are greater than the grout
        // random mask values, so we know which random mask value we should pick from the local neighborhood.
      }
      vec4 randomMaskSample2 = texture2D(dmMainMaskTexture, mainMaskUv + vec2(dmMainMaskTextureInvSize));
      if (randomMaskSample2.x > randomMaskSample.x) {
        randomMaskSample =  randomMaskSample2;
      }
      vec4 randomMaskSample3 = texture2D(dmMainMaskTexture, mainMaskUv + vec2(dmMainMaskTextureInvSize, 0));
      if (randomMaskSample3.x > randomMaskSample.x) {
        randomMaskSample = randomMaskSample3;
      }
      vec4 randomMaskSample4 = texture2D(dmMainMaskTexture, mainMaskUv + vec2(0, dmMainMaskTextureInvSize));
      if (randomMaskSample4.x > randomMaskSample.x) {
        randomMaskSample = randomMaskSample4;
      }
    }
    mainRandomMask = randomMaskSample.x;
    float sdfPatternIDNormIndex = randomMaskSample.y;

#if defined(METAL_SCREEN_MATERIAL)
${
  ""
  // The metal screen material blends between an alpha blended version and alpha masked version.
  // We use per-pixel randomization to weight between the two. Combined with TAA this results in a smooth transition.
  // 40.0 is an arbitrary ad-hoc constant controlling the size of the transition. It's been optimized for visual appearance.
}
      metalScreenBlendingRandomFactor = hash21(gl_FragCoord.xy + randomSeed) * 40.0;
#endif

    if (mainRandomMask < 0.5) {
#ifdef SDF_TRANSPARENT
  #if defined(METAL_SCREEN_MATERIAL)
      float viewDist = length(vViewPosition);
      if (useMetalScreenFineMesh(viewDist)) {
        discard;
      }
  #else
      discard;
  #endif
#endif
      mainRandomMask = ${OUTSIDE_RANDOM_MASK_VALUE.toFixed(1)};
    } else {
      ${
        ""
        // Scramble the mask value to reduce repeating.
        // We're using ivec2 here to make mainRandomMask match exactly across tile boundaries.
        // The "randomMaskUniqueness" scrambling step is very sensitive to changes in the numbers here.
        // randomMaskSample.zw values are set so that we can assign cells that wrap around the texture edge to a specific instance of the texture.
        // Value 0 means that the cell is continuous in that direction, values 1/3 or 1 mean that the cell is part of a neighbor.
        // Add 0.01 before rounding down to fix precision issues.
      }
        ivec2 mainMaskTile = ivec2(floor(mainMaskUv)) + ivec2(randomMaskSample.zw * 3.0 + 0.01);
        mainMaskTile *= ivec2(greaterThan(randomMaskSample.zw, vec2(0.166)));
      #ifdef REPEAT_LAYERS
        mainMaskTile = (mainMaskTile + 200) % dmMainMaskTileRepeatInterval;
      #endif
      // 0.71 and 0.44 are arbitrary constants that make the pattern look non-repeating.
      mainRandomMask = fract(mainRandomMask * 2.0 + float(mainMaskTile.x) * 0.71 + float(mainMaskTile.y) * 0.44) * 0.5 + 0.5;
      int sdfPatternIDIndex = int(sdfPatternIDNormIndex * ${(MAX_SDF_PATTERNS - 1).toFixed(1)} + 0.5);
      sdfPatternIDMask = 1 << sdfPatternIDIndex;
    }
    float depthForMask = parallaxMapUvh.z;
    `;
  } else {
    return `if (dmUseTextureForMainMask) {
      vec2 mainMaskUv = (dmMainMaskUvTransform * vec3(uvWithParallax, 1)).xy;
      mainRandomMask = texture2D(dmMainMaskTexture, mainMaskUv).r;
      if (mainRandomMask < 0.5) {
        mainRandomMask = ${OUTSIDE_RANDOM_MASK_VALUE.toFixed(1)};
        sdfPatternIDMask = 0;
      } else {
        sdfPatternIDMask = 1 << 0;
      }
    }
#ifdef SDF_TRANSPARENT
    if (mainRandomMask < 0.5) {
      discard;
    }
#endif
    `;
  }
}

export function includeMainMaskUniforms(designMaterialShaderChunks: DesignMaterialShaderChunks) {
  designMaterialShaderChunks.fragmentDefinitions.push(
    `uniform bool dmUseTextureForMainMask;
      uniform sampler2D dmMainMaskTexture;
      uniform float dmMainMaskTextureInvSize; // Assumed square
      uniform mat3 dmMainMaskUvTransform;

      #ifdef REPEAT_LAYERS
      uniform int dmMainMaskTileRepeatInterval;
      #endif
      `,
  );
}
